import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LocationCard from '../components/LocationCard';
import { ArrowLeft, TrendingUp, Users, Building2, Download, Share2, BookmarkPlus, BarChart as ChartBar, SlidersHorizontal, ArrowUpDown, Loader } from 'lucide-react';

type SortOption = 'rating' | 'footTraffic' | 'rent' | 'potential';
type FilterOption = 'all' | 'highGrowth' | 'stable' | 'emerging';

const ITEMS_PER_PAGE = 15;

const ResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { searchData } = location.state || {};
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('rating');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const results = [
    {
      id: "1",
      name: "Prime Location A",
      address: "123 Main St, Atlanta, GA",
      rating: 4.8,
      potential: "High Growth",
      image: "https://images.pexels.com/photos/380768/pexels-photo-380768.jpeg",
      metrics: {
        footTraffic: "12,000/day",
        competition: "Low",
        growth: "+15% YoY",
        demographics: {
          age: "25-45",
          income: "$75k-150k",
          education: "Bachelor's or higher"
        },
        marketSize: "$5.2M annually",
        businessTypes: ["Retail", "Food & Beverage", "Professional Services"],
        nearbyAttractions: ["Shopping Mall", "Office Complex", "University"],
        propertyDetails: {
          size: "2,500 sq ft",
          rent: "4500",
          type: "Commercial Space",
          parking: "Available"
        }
      }
    },
    {
      id: "2",
      name: "Downtown Hub",
      address: "456 Commerce Ave, Atlanta, GA",
      rating: 4.5,
      potential: "Stable",
      image: "https://images.pexels.com/photos/1838640/pexels-photo-1838640.jpeg",
      metrics: {
        footTraffic: "18,000/day",
        competition: "Medium",
        growth: "+8% YoY",
        demographics: {
          age: "18-35",
          income: "$50k-100k",
          education: "Mixed"
        },
        marketSize: "$3.8M annually",
        businessTypes: ["Cafes", "Boutiques", "Entertainment"],
        nearbyAttractions: ["Transit Station", "Park", "Entertainment District"],
        propertyDetails: {
          size: "1,800 sq ft",
          rent: "3800",
          type: "Street-level Retail",
          parking: "Street parking"
        }
      }
    },
    {
      id: "3",
      name: "Tech District Space",
      address: "789 Innovation Blvd, Atlanta, GA",
      rating: 4.2,
      potential: "Emerging",
      image: "https://images.pexels.com/photos/3932930/pexels-photo-3932930.jpeg",
      metrics: {
        footTraffic: "8,000/day",
        competition: "Low",
        growth: "+25% YoY",
        demographics: {
          age: "22-40",
          income: "$85k-180k",
          education: "Tech-focused"
        },
        marketSize: "$2.8M annually",
        businessTypes: ["Tech Startups", "Co-working", "Digital Services"],
        nearbyAttractions: ["Tech Hub", "Innovation Center", "Research Lab"],
        propertyDetails: {
          size: "3,200 sq ft",
          rent: "5200",
          type: "Modern Office",
          parking: "Garage"
        }
      }
    },
    {
      id: "4",
      name: "Midtown Plaza",
      address: "321 Peachtree St, Atlanta, GA",
      rating: 4.7,
      potential: "High Growth",
      image: "https://images.pexels.com/photos/2079246/pexels-photo-2079246.jpeg",
      metrics: {
        footTraffic: "15,000/day",
        competition: "Medium",
        growth: "+12% YoY",
        demographics: {
          age: "25-50",
          income: "$65k-130k",
          education: "Mixed"
        },
        marketSize: "$4.5M annually",
        businessTypes: ["Restaurant", "Retail", "Services"],
        nearbyAttractions: ["Hotels", "Convention Center", "Museums"],
        propertyDetails: {
          size: "2,800 sq ft",
          rent: "4800",
          type: "Mixed-Use Space",
          parking: "Dedicated"
        }
      }
    },
    {
      id: "5",
      name: "Historic District Venue",
      address: "567 Heritage Row, Atlanta, GA",
      rating: 4.4,
      potential: "Stable",
      image: "https://images.pexels.com/photos/1115804/pexels-photo-1115804.jpeg",
      metrics: {
        footTraffic: "10,000/day",
        competition: "Low",
        growth: "+7% YoY",
        demographics: {
          age: "30-60",
          income: "$70k-140k",
          education: "Bachelor's or higher"
        },
        marketSize: "$3.2M annually",
        businessTypes: ["Boutique", "Gallery", "Cafe"],
        nearbyAttractions: ["Historic Sites", "Art Galleries", "Boutiques"],
        propertyDetails: {
          size: "2,200 sq ft",
          rent: "4200",
          type: "Historic Building",
          parking: "Street & Garage"
        }
      }
    },
    {
      id: "6",
      name: "Riverside Complex",
      address: "789 River View Dr, Atlanta, GA",
      rating: 4.3,
      potential: "Emerging",
      image: "https://images.pexels.com/photos/323705/pexels-photo-323705.jpeg",
      metrics: {
        footTraffic: "9,000/day",
        competition: "Low",
        growth: "+18% YoY",
        demographics: {
          age: "25-45",
          income: "$60k-120k",
          education: "Mixed"
        },
        marketSize: "$2.9M annually",
        businessTypes: ["Restaurant", "Fitness", "Retail"],
        nearbyAttractions: ["Riverside Park", "Walking Trails", "Marina"],
        propertyDetails: {
          size: "2,600 sq ft",
          rent: "4100",
          type: "Waterfront Property",
          parking: "Large Lot"
        }
      }
    },
    {
      id: "7",
      name: "University Gateway",
      address: "234 Campus Circle, Atlanta, GA",
      rating: 4.6,
      potential: "High Growth",
      image: "https://images.pexels.com/photos/1531677/pexels-photo-1531677.jpeg",
      metrics: {
        footTraffic: "20,000/day",
        competition: "Medium",
        growth: "+14% YoY",
        demographics: {
          age: "18-30",
          income: "$30k-80k",
          education: "College"
        },
        marketSize: "$3.5M annually",
        businessTypes: ["Quick Service", "Books", "Services"],
        nearbyAttractions: ["University", "Student Housing", "Sports Complex"],
        propertyDetails: {
          size: "2,000 sq ft",
          rent: "3900",
          type: "Retail Space",
          parking: "Shared Lot"
        }
      }
    },
    {
      id: "8",
      name: "Arts District Studio",
      address: "901 Creative Way, Atlanta, GA",
      rating: 4.1,
      potential: "Emerging",
      image: "https://images.pexels.com/photos/2079436/pexels-photo-2079436.jpeg",
      metrics: {
        footTraffic: "7,000/day",
        competition: "Low",
        growth: "+22% YoY",
        demographics: {
          age: "20-40",
          income: "$45k-90k",
          education: "Creative/Arts"
        },
        marketSize: "$2.4M annually",
        businessTypes: ["Gallery", "Studio", "Cafe"],
        nearbyAttractions: ["Art Center", "Theater", "Design Studios"],
        propertyDetails: {
          size: "1,900 sq ft",
          rent: "3400",
          type: "Creative Space",
          parking: "Street"
        }
      }
    }
  ];

  const sortedAndFilteredResults = useMemo(() => {
    let filteredResults = [...results];
    
    if (filterBy !== 'all') {
      filteredResults = filteredResults.filter(result => {
        switch (filterBy) {
          case 'highGrowth':
            return result.potential === 'High Growth';
          case 'stable':
            return result.potential === 'Stable';
          case 'emerging':
            return result.potential === 'Emerging';
          default:
            return true;
        }
      });
    }

    return filteredResults.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'footTraffic':
          return parseInt(b.metrics.footTraffic) - parseInt(a.metrics.footTraffic);
        case 'rent':
          return parseInt(b.metrics.propertyDetails.rent) - parseInt(a.metrics.propertyDetails.rent);
        case 'potential':
          return b.metrics.growth.localeCompare(a.metrics.growth);
        default:
          return 0;
      }
    });
  }, [results, sortBy, filterBy]);

  const paginatedResults = useMemo(() => {
    return sortedAndFilteredResults.slice(0, page * ITEMS_PER_PAGE);
  }, [sortedAndFilteredResults, page]);

  const hasMoreResults = paginatedResults.length < sortedAndFilteredResults.length;

  const handleLoadMore = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setPage(prev => prev + 1);
    setLoading(false);
  };

  const handleSaveLocation = (locationId: string) => {
    setSelectedLocation(locationId);
  };

  const handleDownloadReport = (locationId: string) => {
    console.log('Downloading report for location:', locationId);
  };

  const handleShare = (locationId: string) => {
    const shareData = {
      title: 'Location Scout - Location Analysis',
      text: 'Check out this potential business location!',
      url: window.location.href
    };
    
    if (navigator.share) {
      navigator.share(shareData);
    }
  };

  const handleViewAnalytics = (locationId: string) => {
    navigate(`/analytics/${locationId}`);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Search
        </button>
        
        <div className="text-sm text-muted-foreground">
          Results for: {searchData?.location}
        </div>
      </div>

      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center text-muted-foreground hover:text-foreground"
          >
            <SlidersHorizontal size={20} className="mr-2" />
            Filters & Sort
          </button>
          <div className="text-sm text-muted-foreground">
            {sortedAndFilteredResults.length} locations found
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Sort by</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="w-full bg-card border border-border rounded-lg p-2"
              >
                <option value="rating">Rating</option>
                <option value="footTraffic">Foot Traffic</option>
                <option value="rent">Rent</option>
                <option value="potential">Growth Potential</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Filter by Potential</label>
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value as FilterOption)}
                className="w-full bg-card border border-border rounded-lg p-2"
              >
                <option value="all">All Locations</option>
                <option value="highGrowth">High Growth</option>
                <option value="stable">Stable</option>
                <option value="emerging">Emerging</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {paginatedResults.map((result) => (
          <div key={result.id} className="space-y-4">
            <LocationCard {...result} />
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Demographics</h3>
                <div className="space-y-2 text-sm">
                  <p>Age: {result.metrics.demographics.age}</p>
                  <p>Income: {result.metrics.demographics.income}</p>
                  <p>Education: {result.metrics.demographics.education}</p>
                </div>
              </div>
              
              <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Market</h3>
                <div className="space-y-2 text-sm">
                  <p>Size: {result.metrics.marketSize}</p>
                  <p>Growth: {result.metrics.growth}</p>
                  <p>Competition: {result.metrics.competition}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Property Details</h3>
                <div className="space-y-2 text-sm">
                  <p>Size: {result.metrics.propertyDetails.size}</p>
                  <p>Rent: ${result.metrics.propertyDetails.rent}/month</p>
                  <p>Type: {result.metrics.propertyDetails.type}</p>
                </div>
              </div>
              
              <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Nearby</h3>
                <div className="space-y-2 text-sm">
                  <p>{result.metrics.nearbyAttractions.join(', ')}</p>
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => handleSaveLocation(result.id)}
                className="flex-1 bg-primary text-primary-foreground py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center"
              >
                <BookmarkPlus size={20} className="mr-2" />
                Save Location
              </button>
              
              <button
                onClick={() => handleDownloadReport(result.id)}
                className="flex-1 bg-secondary text-secondary-foreground py-2 px-4 rounded-lg hover:bg-secondary/90 transition-colors flex items-center justify-center"
              >
                <Download size={20} className="mr-2" />
                Download Report
              </button>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => handleShare(result.id)}
                className="flex-1 bg-accent text-accent-foreground py-2 px-4 rounded-lg hover:bg-accent/90 transition-colors flex items-center justify-center"
              >
                <Share2 size={20} className="mr-2" />
                Share
              </button>
              
              <button
                onClick={() => handleViewAnalytics(result.id)}
                className="flex-1 border border-border py-2 px-4 rounded-lg hover:bg-card/50 transition-colors flex items-center justify-center"
              >
                <ChartBar size={20} className="mr-2" />
                View Analytics
              </button>
            </div>
          </div>
        ))}
      </div>

      {hasMoreResults && (
        <div className="flex justify-center mt-8">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="bg-primary text-primary-foreground px-8 py-3 rounded-lg hover:bg-primary/90 transition-colors flex items-center disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader className="animate-spin mr-2" size={20} />
                Loading...
              </>
            ) : (
              'Load More Results'
            )}
          </button>
        </div>
      )}

      <div className="mt-8 p-6 bg-card/50 backdrop-blur-sm border border-border rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Next Steps</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/schedule-visit')}
            className="p-4 bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors text-left"
          >
            <h3 className="font-semibold">Schedule a Visit</h3>
            <p className="text-sm text-muted-foreground">Book an appointment to view the location</p>
          </button>
          
          <button
            onClick={() => navigate('/connect-expert')}
            className="p-4 bg-secondary/10 rounded-lg hover:bg-secondary/20 transition-colors text-left"
          >
            <h3 className="font-semibold">Connect with Expert</h3>
            <p className="text-sm text-muted-foreground">Get professional guidance</p>
          </button>
          
          <button
            onClick={() => navigate('/financing-options')}
            className="p-4 bg-accent/10 rounded-lg hover:bg-accent/20 transition-colors text-left"
          >
            <h3 className="font-semibold">Explore Financing</h3>
            <p className="text-sm text-muted-foreground">Find funding options</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;